import { useCallback, useMemo, type ComponentType } from "react";

import { HISTORY_IMPORT_DIALOG_CLASS_NAME } from "@/routes/smart-table/utils/historyImportTypes";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import type { HistoryImportConfig } from "@/components/ui/ag-grid-table";

export interface HistoryImportContentProps<TPayload> {
  importConfig: HistoryImportConfig<TPayload>;
  tableId?: string;
  sheetId?: string;
  targetColumnId?: string | null;
}

interface AddFromHistoryDialogProps<TPayload> {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  isImporting: boolean;
  importProgress: number | null;
  onImport: (payload: TPayload) => Promise<boolean>;
  selectionLimit?: 1;
  actionLabel?: string;
  tableId?: string;
  sheetId?: string;
  targetColumnId?: string | null;
  Content: ComponentType<HistoryImportContentProps<TPayload>>;
}

const AddFromHistoryDialog = <TPayload,>({
  open,
  onOpenChange,
  title,
  isImporting,
  importProgress,
  onImport,
  selectionLimit,
  actionLabel,
  tableId,
  sheetId,
  targetColumnId,
  Content,
}: AddFromHistoryDialogProps<TPayload>) => {
  const handleImportSuccess = useCallback(() => onOpenChange(false), [onOpenChange]);
  const importConfig = useMemo<HistoryImportConfig<TPayload>>(
    () => ({
      submitImport: onImport,
      isImporting,
      importProgress,
      handleImportSuccess,
      selectionLimit,
      actionLabel,
    }),
    [actionLabel, handleImportSuccess, importProgress, isImporting, onImport, selectionLimit],
  );

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className={HISTORY_IMPORT_DIALOG_CLASS_NAME}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
        </DialogHeader>
        <div className="min-h-0 flex-1">
          {open ? (
            <Content
              importConfig={importConfig}
              tableId={tableId}
              sheetId={sheetId}
              targetColumnId={targetColumnId}
            />
          ) : null}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default AddFromHistoryDialog;
